<template>
	<div class="inner-page bulk-add-users">
		<AppHead title="Add New Users" />
		<PageHeader 
			title="Add New Users"
			desc="Bulk add as many users as needed."
			helpTitle="Welcome to 'Add New Users'"
			helpDesc="Within this page you can add as many new users as you need to the portal."
			:hr="true"
			>
			<Button
				label="Show users I have invited"
				color="blue"
				size="regular"
				icon="users"
				:buttonRoute="`/admin/user/${$page.props.user.id}/attached-users`"
				/>
		</PageHeader>

		<div class="bulk-add-users-grid">

			<template v-if="newUsers.length > 0">

				<div 
					class="new-user-block"
					v-for="(user, index) in newUsers"
					:key="index"
					>
					
					<div class="new-user-block-inner-grid">
						<div class="new-user-block-inner-grid-item">
							<InputText
								label="First name"
								placeholder="First name"
								type="text"
								v-model="user.firstName.value"
								autocomplete="disabled"
								:noMargin="true"
								@keyup="checkIfInputsAreValid(false)"
								:isError="indicateFieldError(user.firstName)"
								/>
						</div>
						<div class="new-user-block-inner-grid-item">
							<InputText
								label="Last name"
								placeholder="Last name"
								type="text"
								v-model="user.lastName.value"
								autocomplete="disabled"
								:noMargin="true"
								@keyup="checkIfInputsAreValid(false)"
								:isError="indicateFieldError(user.lastName)"
								/>
						</div>
						<div class="new-user-block-inner-grid-item">
							<InputText
								label="Email"
								placeholder="Email"
								type="text"
								v-model="user.email.value"
								autocomplete="disabled"
								:noMargin="true"
								@keyup="checkIfInputsAreValid(false)"
								:isError="indicateFieldError(user.email)"
								/>
						</div>

						<div 
							class="new-user-block-inner-grid-item select-input"
							:class="{
								'is-error': indicateFieldError(user.userType)
							}"
							>
							<label>User Type</label>
							<select 
								v-model="user.userType.value"
								@change="checkIfInputsAreValid(false)"
								>
								<option 
									v-for="(userType, index) in userTypeOptions"
									:value="userType.value"
									>
									{{ userType.label }}
								</option>
							</select>
						</div>

						<div 
							class="new-user-block-inner-grid-item select-input"
							:class="{
								'is-error': indicateFieldError(user.tag)
							}"
							>
							<label>User Tag</label>
							<select 
								v-model="user.tag.value"
								@change="checkIfInputsAreValid(false)"
								>
								<option 
									v-for="(tag, index) in userTags"
									:value="tag.value"
									>
									{{ tag.label }}
								</option>
							</select>
						</div>

						<div class="new-user-block-inner-grid-item remove-col">
							<Button
								color="red"
								size="small"
								icon="trash"
								@click="newUsers.splice(index, 1)"
								/>
						</div>
						
					</div>
				</div>

			</template>

			<Placeholder 
				v-else
				message="Add a new user by clicking the 'New row' button below."
				/>

			<div class="buttons-container">
				<Button
					label="New row"
					color="blue"
					size="large"
					icon="plus"
					@click="addNewUser"
					/>
				<Button
					label="Send invites"
					color="green"
					size="large"
					icon="paper-plane"
					@click="inviteUsers"
					:disabled="!areInputsValid"
					/>
			</div>

		</div>

	</div>
</template>

<script>

import axios from 'axios'

import AppHead from "@/Components/AppHead"
import AppLayout from "@/Layouts/AppLayout.vue"
import PageHeader from "@/Components/PageHeader"
import Button from '@/Components/Button'
import InputText from "@/Components/InputText"
import Placeholder from "@/Components/Placeholder"

export default {
	layout: AppLayout,
	components: {
		AppHead,
		PageHeader,
		Button,
		InputText,
		Placeholder
	},
	title: 'Add new users',
	data: () => {
		return {
			newUsers: [],
			userTags: [
				{
					value: null,
					label: 'None',
				},
				{
					value: 'rep',
					label: 'Rep'
				},
				{
					value: 'surgeon',
					label: 'Surgeon'
				},
			],
			userTypeOptions: [
				{
					value: 'admin',
					label: 'Super Admin'
				},
				{
					value: 'user',
					label: 'User'
				},
			],
			areInputsValid: false,
			globalForceError: false
		}
	},
	mounted(){

		
	},
	methods: {
		addNewUser() {
			this.newUsers.push({
				firstName: {
					value: '',
					valid: false,
					forceError: false
				},
				lastName: {
					value: '',
					valid: false,
					forceError: false
				},
				email: {
					value: '',
					valid: false,
					forceError: false
				},
				userType: {
					value: 'user',
					valid: true,
					forceError: false
				},
				tag: {
					value: null,
					valid: true,
					forceError: false
				},
			})
		},
		inviteUsers() {

			this.checkIfInputsAreValid(true)

			this.$nextTick(() => {
				if(this.areInputsValid) {
					// console.log('send invites', this.newUsers)

					// send invites
					axios.post('/api/bulk-add-users', {
						users: this.newUsers
					})
						.then((res) => {
							// console.log('res', res)
							if(res.data.success) {
								this.newUsers = []
								this.$toast.success('Invites sent successfully')
							} else {
								
								if(res.data.existing_emails) {

									// loop through newUsers and mark invalid any item that is in existing_emails
									for(let i = 0; i < this.newUsers.length; i++) {
										if(res.data.existing_emails.includes(this.newUsers[i].email.value)) {
											this.newUsers[i].email.valid = false
											this.newUsers[i].email.forceError = true
										}
									}

									this.$toast.error('Some of the emails you entered already exist in the system')
								} else {
									this.$toast.error('There was an error sending invites')
								}
								
							}
						})
						.catch((err) => {
							// console.log('err', err)
							this.$toast.error('There was an error sending invites')
						})

				}else{
					// console.log('cannot send invites')
					this.$toast.error('Please ensure that all fields are filled in correctly')
				}
			})
		},
		checkIfInputsAreValid(forceError = false) {

			// forceError is used to force the error state on all inputs, usually when the invite users button has been pressed. After that point we want all incorrect fields to be highlighted red.
			if(this.globalForceError){
				forceError = true
			}
			if(forceError){
				this.globalForceError = true
			}

			for(let i = 0; i < this.newUsers.length; i++) {
				
				// check each input and see if it's valid

				// check if first name is at least 2 characters
				if(this.newUsers[i].firstName.value.length >= 2) {
					this.newUsers[i].firstName.valid = true
					this.newUsers[i].firstName.forceError = false
				} else {
					this.newUsers[i].firstName.valid = false

					if(forceError){
						this.newUsers[i].firstName.forceError = true
					}else{
						this.newUsers[i].firstName.forceError = false
					}
				}

				// check if last name is at least 2 characters
				if(this.newUsers[i].lastName.value.length >= 2) {
					this.newUsers[i].lastName.valid = true
					this.newUsers[i].lastName.forceError = false
				} else {
					this.newUsers[i].lastName.valid = false

					if(forceError){
						this.newUsers[i].lastName.forceError = true
					}else{
						this.newUsers[i].lastName.forceError = false
					}
				}

				// check if userType is at least 2 characters
				if(this.newUsers[i].userType.value.length >= 2 || this.newUsers[i].userType.value === false) {
					this.newUsers[i].userType.valid = true
					this.newUsers[i].userType.forceError = false
				} else {
					this.newUsers[i].userType.valid = false

					if(forceError){
						this.newUsers[i].userType.forceError = true
					}else{
						this.newUsers[i].userType.forceError = false
					}
				}

				// check if email is of an email format
				const email = this.newUsers[i].email.value
				const emailRegex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
				const isEmailValid = emailRegex.test(email)
				if(isEmailValid) {
					this.newUsers[i].email.valid = true
					this.newUsers[i].email.forceError = false
				} else {
					this.newUsers[i].email.valid = false

					if(forceError){
						this.newUsers[i].email.forceError = true
					}else{
						this.newUsers[i].email.forceError = false
					}
				}

				// if all inputs are valid then enable the invite users button
				if(this.newUsers[i].firstName.valid && this.newUsers[i].lastName.valid && this.newUsers[i].email.valid) {
					this.areInputsValid = true
				} else {
					this.areInputsValid = false
				}
				
			}

		},
		indicateFieldError(field) {
			if((!field.valid && field.value.length > 0) || field.forceError) {
				return true
			} else {
				return false
			}
		}
	}
}

</script>

<style lang="sass">

.bulk-add-users
	.bulk-add-users-grid
		display: flex
		flex-direction: column
		gap: 20px

		.new-user-block-inner-grid
			padding: 15px
			border-radius: 8px
			box-shadow: 0 0 10px rgba(0,0,0,0.1)
			background-color: #fff
			display: grid
			grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr))
			gap: 10px
			position: relative

			.remove-col
				position: absolute
				top: -5px
				right: -5px

		.select-input
			margin: 0
			display: flex
			flex-direction: column
			width: 100%
			// max-width: 30rem
			
			label
				font-family: 'Roboto Condensed', sans-serif
				font-weight: 700
				font-size: 16px
				line-height: 1.1
				color: #252525
				margin-bottom: 10px

			select
				border-radius: 3px
				border: 1px solid lighten(#111, 70%)
				box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05)

				&:disabled
					background-color: lighten(#111, 90%)
					color: lighten(#111, 70%)
					cursor: not-allowed

			&.is-error
				select
					border-color: #ff0000
					background-color: rgba(#ff0000, 0.02)
					color: #ff0000

					&::placeholder
						color: rgba(#ff0000, 0.6)

		.buttons-container
			text-align: center
			display: flex
			justify-content: space-between
			gap: 20px

			@media (max-width: 768px)
				flex-direction: column

				.button
					width: 100%

</style>